import {
  type FC,
  type MouseEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { cn } from '@bridebook/aura';
import { BackButton } from './back-button';
import { NavigationOption } from './navigation-option';
import { type TNavigationOptions, type TUINavigationOptions } from './types';
import { flattenNavigationOptions } from './utils';

interface IPath {
  id: string;
  text: string;
}

const CurrentNestedNavigationListPathContext = createContext<IPath[]>([]);

interface IProps {
  options: TNavigationOptions;
  isOpen: boolean;
  className?: string;
}

export const NestedNavigationList: FC<IProps> = ({ options, isOpen, className = '' }) => {
  const [path, setPath] = useState<IPath[]>([]);
  const uiNavigationOptions = useMemo(() => flattenNavigationOptions(options), [options]);
  const navigateOneStepBack: MouseEventHandler<HTMLDivElement> = useCallback((event) => {
    setPath((path) => path.slice(0, path.length - 1));
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const navigateForward = useCallback((newPath: IPath) => {
    setPath((path) => [...path, newPath]);
  }, []);

  useEffect(() => {
    if (path.length === 0) return;
    // reset path when provided options change to prevent situations where the user is left with an empty menu
    setPath([]);
    // this effect must only fire when provided options change, not when users simply navigate the menu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const isBackButtonVisible = path.length > 0;
  return (
    <CurrentNestedNavigationListPathContext.Provider value={path}>
      <div className={cn(className, 'bcw')} data-shown={isOpen} data-back={isBackButtonVisible}>
        {isBackButtonVisible && <BackButton onClick={navigateOneStepBack} label={path[0].text} />}
        <div className="bcob" data-back={isBackButtonVisible}>
          <NavigationOptions
            options={uiNavigationOptions}
            navigateForward={navigateForward}
            isOpen={isOpen}
          />
        </div>
      </div>
    </CurrentNestedNavigationListPathContext.Provider>
  );
};

interface INavigationOptionsProps {
  options: TUINavigationOptions;
  navigateForward: (s: IPath) => void;
  isOpen: boolean;
}

const NavigationOptions: FC<INavigationOptionsProps> = ({ options, navigateForward, isOpen }) => {
  const currentNavigationPathContext = useContext(CurrentNestedNavigationListPathContext);
  const currentPath = `/${currentNavigationPathContext.map((p) => p.id).join('/')}`;
  return (
    <>
      {Object.entries(options).flatMap(([path, options]) =>
        options.map((option, index) => (
          <NavigationOption
            option={option}
            key={option.id}
            onClick={() => navigateForward({ id: option.id, text: option.text })}
            isShown={path === currentPath && isOpen}
            isNotFirstVisibleItem={index !== 0}
          />
        )),
      )}
    </>
  );
};
